import { fetchClient } from "@/api/api";
import type {
	AdminBanUserByIdInput,
	AdminBanUserByIdOutput,
	AdminBanUsersInput,
	AdminBanUsersOutput,
	AdminGetManualTransactionFacetsInput,
	AdminGetManualTransactionFacetsOutput,
	AdminGetManualTransactionsInput,
	AdminGetManualTransactionsOutput,
	AdminResetPasswordInput,
	AdminResetPasswordOutput,
	AdminUnbanUserByIdInput,
	AdminUnbanUserByIdOutput,
	AdminUnbanUsersInput,
	AdminUnbanUsersOutput,
	AdminUpdateFcUserManualTransactionInput,
	AdminUpdateFcUserManualTransactionOutput,
	adminUpdateFcUserInput,
	adminUpdateFcUserOutput,
	getAdminFcUsersInput,
	getAdminFcUsersOutput,
	getAdminSenderGroupsOutput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import { createFacetSource } from "@/services/facets";
import type { ApiError } from "@/utils/fetching";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { invalidateQueries } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PAYMENT_OPERATORS_SERVICE_KEYS } from "../payment_operators";
import { ADMIN_FC_USERS_SERVICE_KEYS } from "./keys";

export const useGetAdminFcUsers: CustomQueryHook<
	getAdminFcUsersOutput,
	getAdminFcUsersInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: ADMIN_FC_USERS_SERVICE_KEYS.useGetAdminFcUsers(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/admin/users/", {
				params: { query: payload },
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
	});
};

export const useAdminUpdateFcUser: CustomMutationHook<
	adminUpdateFcUserInput,
	adminUpdateFcUserOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<adminUpdateFcUserOutput, ApiError, adminUpdateFcUserInput>(
		{
			mutationKey: ADMIN_FC_USERS_SERVICE_KEYS.useAdminUpdateFcUser(),
			mutationFn: async (payload) => {
				const { user_id, ...body } = payload;
				const { data, error } = await fetchClient.PATCH(
					"/v1/fc/admin/users/{user_id}",
					{
						params: { path: { user_id } },
						body,
						throwOnError: true,
					},
				);
				if (error) throw error;
				return data;
			},
			onSuccess: () => {
				invalidateQueries(ADMIN_FC_USERS_SERVICE_KEYS.useGetAdminFcUsers());
			},
			onError: (error) => {
				toastApiError(error);
			},
		},
	);
};

export const useAdminUpdateFcUserManualTransaction: CustomMutationHook<
	AdminUpdateFcUserManualTransactionInput,
	AdminUpdateFcUserManualTransactionOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		AdminUpdateFcUserManualTransactionOutput,
		ApiError,
		AdminUpdateFcUserManualTransactionInput
	>({
		mutationKey:
			ADMIN_FC_USERS_SERVICE_KEYS.useAdminUpdateFcUserManualTransaction(),
		mutationFn: async (payload) => {
			const { user_id, ...body } = payload;
			const { data, error } = await fetchClient.POST(
				"/v1/fc/manual-transaction/{user_id}",
				{
					params: { path: { user_id } },
					body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(ADMIN_FC_USERS_SERVICE_KEYS.useGetAdminFcUsers());
			invalidateQueries(ADMIN_FC_USERS_SERVICE_KEYS.useGetManualTransactions());
			invalidateQueries(
				ADMIN_FC_USERS_SERVICE_KEYS.useGetManualTransactionFacets(),
			);
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useAdminUserResetPassword: CustomMutationHook<
	AdminResetPasswordInput,
	AdminResetPasswordOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		AdminResetPasswordOutput,
		ApiError,
		AdminResetPasswordInput
	>({
		mutationKey: ADMIN_FC_USERS_SERVICE_KEYS.useAdminUserResetPassword(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/admin/reset-password",
				{
					body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(ADMIN_FC_USERS_SERVICE_KEYS.useGetAdminFcUsers());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useGetManualTransactions: CustomQueryHook<
	AdminGetManualTransactionsOutput,
	AdminGetManualTransactionsInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: ADMIN_FC_USERS_SERVICE_KEYS.useGetManualTransactions(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/manual-transaction/",
				{
					params: { query: payload },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

export const useGetManualTransactionFacets: CustomQueryHook<
	AdminGetManualTransactionFacetsOutput,
	AdminGetManualTransactionFacetsInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey:
			ADMIN_FC_USERS_SERVICE_KEYS.useGetManualTransactionFacets(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/manual-transaction/facets/",
				{
					params: { query: payload },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

export const useAdminBanUsers: CustomMutationHook<
	AdminBanUsersInput,
	AdminBanUsersOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<AdminBanUsersOutput, ApiError, AdminBanUsersInput>({
		mutationKey: ADMIN_FC_USERS_SERVICE_KEYS.useAdminBanUsers(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST("/v1/fc/admin/users/ban", {
				body,
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(ADMIN_FC_USERS_SERVICE_KEYS.useGetAdminFcUsers());
			invalidateQueries(
				PAYMENT_OPERATORS_SERVICE_KEYS.useListPaymentOperators(),
			);
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useAdminUnbanUsers: CustomMutationHook<
	AdminUnbanUsersInput,
	AdminUnbanUsersOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<AdminUnbanUsersOutput, ApiError, AdminUnbanUsersInput>({
		mutationKey: ADMIN_FC_USERS_SERVICE_KEYS.useAdminUnbanUsers(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/admin/users/unban",
				{
					body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(ADMIN_FC_USERS_SERVICE_KEYS.useGetAdminFcUsers());
			invalidateQueries(
				PAYMENT_OPERATORS_SERVICE_KEYS.useListPaymentOperators(),
			);
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useAdminBanUserById: CustomMutationHook<
	AdminBanUserByIdInput,
	AdminBanUserByIdOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<AdminBanUserByIdOutput, ApiError, AdminBanUserByIdInput>({
		mutationKey: ADMIN_FC_USERS_SERVICE_KEYS.useAdminBanUserById(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/admin/users/{user_id}/ban",
				{
					params: { path: payload },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(ADMIN_FC_USERS_SERVICE_KEYS.useGetAdminFcUsers());
			invalidateQueries(
				PAYMENT_OPERATORS_SERVICE_KEYS.useListPaymentOperators(),
			);
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useAdminUnbanUserById: CustomMutationHook<
	AdminUnbanUserByIdInput,
	AdminUnbanUserByIdOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		AdminUnbanUserByIdOutput,
		ApiError,
		AdminUnbanUserByIdInput
	>({
		mutationKey: ADMIN_FC_USERS_SERVICE_KEYS.useAdminUnbanUserById(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/admin/users/{user_id}/unban",
				{
					params: { path: payload },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(ADMIN_FC_USERS_SERVICE_KEYS.useGetAdminFcUsers());
			invalidateQueries(
				PAYMENT_OPERATORS_SERVICE_KEYS.useListPaymentOperators(),
			);
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useGetAdminSenderGroups: CustomQueryHook<
	getAdminSenderGroupsOutput,
	undefined
> = (options) => {
	return useQuery({
		...options,
		queryKey: ADMIN_FC_USERS_SERVICE_KEYS.useGetAdminSenderGroups(),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/admin/sender-groups/",
				{
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

export const MANUAL_TRANSACTION_FACET_SOURCE =
	createFacetSource<AdminGetManualTransactionFacetsOutput>({
		path: "/v1/fc/manual-transaction/facets/",
		queryKey: (query) =>
			ADMIN_FC_USERS_SERVICE_KEYS.useGetManualTransactionFacets(query as any),
	});
